import { useState, type MouseEvent } from 'react';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';
import { persistPreferredLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { supportedLocales, type Locale } from 'lib/i18n/config';
import { getTranslations } from 'lib/i18n/dictionary';
import { stripLocalePrefix, withLocale } from 'lib/i18n/path';
import { useLocation, useNavigate } from 'react-router-dom';

interface AdminLocaleSwitcherProps {
  light?: boolean;
}

export function AdminLocaleSwitcher({ light = false }: AdminLocaleSwitcherProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLocaleChange = (nextLocale: Locale) => {
    handleClose();

    if (nextLocale === locale) {
      return;
    }

    persistPreferredLocale(nextLocale);

    const { pathname } = stripLocalePrefix(location.pathname);
    const nextPath = withLocale(
      `${pathname}${location.search || ''}${location.hash || ''}`,
      nextLocale
    );

    navigate(nextPath, { replace: true });
  };

  return (
    <>
      <Tooltip title={translations.localeSwitcher.label}>
        <SoftButton
          onClick={handleOpen}
          aria-label={translations.localeSwitcher.label}
          aria-controls={anchorEl ? 'admin-locale-menu' : undefined}
          aria-haspopup="menu"
          aria-expanded={anchorEl ? 'true' : undefined}
          size="small"
          variant="outlined"
          color="primary"
          sx={(theme) => ({
            width: 34,
            height: 34,
            minWidth: 34,
            padding: 0,
            borderRadius: '50%',
            boxShadow: 'none',
            backgroundColor: light ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
            color: light ? '#ffffff' : theme.palette.primary.main,
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: light ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
            },
          })}
        >
          <SoftTypography
            variant="button"
            fontWeight="regular"
            color="inherit"
            sx={{ letterSpacing: '0.08em', lineHeight: 1 }}
          >
            {locale.toUpperCase()}
          </SoftTypography>
        </SoftButton>
      </Tooltip>

      <Menu
        id="admin-locale-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
          },
        }}
      >
        {supportedLocales.map((candidateLocale) => {
          const candidateTranslations = getTranslations(candidateLocale);
          const isActive = candidateLocale === locale;

          return (
            <MenuItem
              key={candidateLocale}
              selected={isActive}
              onClick={() => handleLocaleChange(candidateLocale)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <SoftTypography
                variant="button"
                fontWeight={isActive ? 'bold' : 'regular'}
              >
                {candidateTranslations.languageNativeName}
              </SoftTypography>
              {isActive ? (
                <Icon
                  fontSize="small"
                  color="info"
                  sx={{ marginLeft: 'auto' }}
                >
                  check
                </Icon>
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
